import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with credentials
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercept response errors and forward them to the frontend logger.
// The logger import must be lazy to avoid a circular-dep chain.
api.interceptors.response.use(
  res => res,
  err => {
    try {
      const status  = err?.response?.status || 0;
      const url     = err?.config?.url || '';
      const message = err?.response?.data?.detail || err?.message || 'Unknown error';
      // Don't log:
      //  • 401s — normal auth redirects
      //  • log-endpoint calls — infinite loop
      //  • AbortController / axios-cancel — intentional client-side abort, not an error
      const isCanceled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' ||
                         (typeof err?.message === 'string' && err.message.toLowerCase() === 'canceled');
      if (status !== 401 && !isCanceled && !url.includes('/logs/')) {
        import('./logger').then(m => m.default.apiError(url, status, message));
      }
    } catch (_) {}
    return Promise.reject(err);
  }
);

// Logs API (admin)
export const logsApi = {
  getEntries:       (params) => api.get('/logs/entries', { params }),
  getSummary:       (days = 7) => api.get('/logs/summary', { params: { days } }),
  getDates:         () => api.get('/logs/dates'),
  getCriticalAlerts:(dismissed = false) => api.get('/logs/critical-alerts', { params: { dismissed } }),
  dismissAlert:     (alertId) => api.post(`/logs/critical-alerts/${alertId}/dismiss`),
  dismissAll:       () => api.post('/logs/critical-alerts/dismiss-all'),
  getStats:         (date) => api.get(`/logs/stats/${date}`),
  export:           (date) => api.get(`/logs/export/${date}`, { responseType: 'blob' }),
};

// Languages API
export const languagesApi = {
  getAll: () => api.get('/languages'),
  getById: (id) => api.get(`/languages/${id}`),
  getByCode: (code) => api.get(`/languages/by-code/${code}`),
  create: (data) => api.post('/languages', data),
  getUserLanguages: () => api.get('/languages/user/me'),
  addUserLanguage: (data) => api.post('/languages/user/me', data),
  removeUserLanguage: (languageId) => api.delete(`/languages/user/me/${languageId}`),
  previewUserLanguageDeletion: (languageId) => api.get(`/languages/user/me/${languageId}/delete-preview`),
  updateProficiency: (languageId, proficiency) => api.patch(`/languages/user/me/${languageId}/proficiency`, { proficiency }),
  hideUserLanguage: (languageId, hide = true) => api.patch(`/languages/user/me/${languageId}/hide`, { hide }),
  getHiddenLanguages: () => api.get('/languages/user/me/hidden'),
  getPopular: () => api.get('/languages/popular'),
};

// Texts API
export const textsApi = {
  getAll: (params = {}) => api.get('/texts', { params }),
  getRandomLesson: () => api.get('/texts/random-lesson'),
  getById: (id) => api.get(`/texts/${id}`),
  create: (data) => api.post('/texts', data),
  update: (id, data) => api.put(`/texts/${id}`, data),
  delete: (id) => api.delete(`/texts/${id}`),
  tokenize: (id) => api.post(`/texts/${id}/tokenize`),
  getTokens: (id) => api.get(`/texts/${id}/tokens`),
  getReaderData: (id, config = {}) => api.get(`/texts/${id}/reader`, { ...config, params: { token_limit: 2000, ...config.params } }),
  getCompletion: (id) => api.get(`/texts/${id}/completion`),
  getUnlockedWords: (id) => api.get(`/texts/${id}/unlocked-words`),
  /**
   * Generate a difficulty-adjusted version of the text and save it as a new text in the user's library.
   * @param {string} id   source text id
   * @param {'i+1'|'i-1'} direction  'i+1' = slightly harder, 'i-1' = slightly easier
   */
  generateAdaptive: (id, direction = 'i-1') => api.post(`/texts/${id}/adaptive`, { direction }),
  extractFromUrl: (url) => api.post('/texts/extract-url', null, { params: { url } }),
  generateFromVocabulary: (languageId, wordCount = 200) => api.post('/texts/generate-from-vocabulary', { language_id: languageId, word_count: wordCount }),
  generateFromTopic: (languageId, topic, wordCount = 200) => api.post('/texts/generate-from-topic', { language_id: languageId, topic, word_count: wordCount }),
  generateBatchFromTopic: (languageConfigOrIds, topic, wordCount = 250) => {
    // Determine if we're sending the old format (list of IDs) or new format (list of config objects)
    const payload = { topic, word_count: wordCount };
    if (languageConfigOrIds.length > 0 && typeof languageConfigOrIds[0] === 'object') {
      payload.languages = languageConfigOrIds;
    } else {
      payload.language_ids = languageConfigOrIds;
    }
    return api.post('/texts/generate-batch-from-topic', payload);
  },
  // Completion & tracking
  finishText: (id) => api.post(`/texts/${id}/finish`),
  generateFollowUp: (sourceTextId, languageId) => api.post('/texts/generate-from-vocabulary', { language_id: languageId, word_count: 200, source_text_id: sourceTextId }, { timeout: 115000 }),
  openSeriesEpisode: (seriesId, episodeId) => api.post(`/series/${encodeURIComponent(seriesId)}/episodes/${encodeURIComponent(episodeId)}/open`),
  openForgeEpisode: (languageCode, episode) => api.post(`/forge-stories/episode/${encodeURIComponent(languageCode)}/${episode}/import-to-reader`),
  trackListen: (data) => api.post('/texts/track/listen', data),
  trackRead: (textId, progressPercent) => api.post('/texts/track/read', { text_id: textId, progress_percent: progressPercent }),
  // Audio generation
  generateAudio: (id, voice = 'female') => api.post(`/texts/${id}/generate-audio`, { voice }),
  generateAudioBoth: (id) => api.post(`/texts/${id}/generate-audio-both`),
  deleteAudio: (id) => api.delete(`/texts/${id}/audio`),
  generateSentenceAudio: (id, voice = 'female') => api.post(`/texts/${id}/generate-sentence-audio`, { voice }),
  translateSentence: (text, sourceLang) => api.post('/texts/translate-sentence', { text, source_lang: sourceLang }),
  getAudioMetadata: (id) => api.get(`/texts/${id}/audio-metadata`),
  getNextAudio: (id, data) => api.post(`/texts/${id}/get-next-audio`, data),
  getAudioProgress: (id) => api.get(`/texts/${id}/audio-progress`),
  downloadFullAudio: (id) => api.get(`/texts/${id}/download-full-audio`, { responseType: 'blob' }),
  // File upload
  extractFromFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/texts/extract-file', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Public texts
  getPublicTexts: (languageId) => api.get(`/texts/public/${languageId}`),
};

// Vocabulary API
export const vocabularyApi = {
  getAll: (params = {}) => api.get('/vocabulary', { params }),
  getCount: (params = {}) => api.get('/vocabulary/count', { params }),
  getLanguages: () => api.get('/vocabulary/languages'),
  getById: (id) => api.get(`/vocabulary/${id}`),
  clickToken: (tokenId) => api.post(`/vocabulary/token/${tokenId}/click`),
  markKnown: (tokenId) => api.post(`/vocabulary/token/${tokenId}/mark-known`),
  ignoreToken: (tokenId) => api.post(`/vocabulary/token/${tokenId}/ignore`),
  setStrength: (tokenId, strength) => api.post(`/vocabulary/token/${tokenId}/set-strength`, null, { params: { strength } }),
  addNote: (stateId, noteText) => api.post(`/vocabulary/${stateId}/note`, null, { params: { note_text: noteText } }),
  addTranslation: (stateId, translation) => api.post(`/vocabulary/${stateId}/translation`, null, { params: { translation } }),
  deleteNote: (stateId) => api.delete(`/vocabulary/${stateId}/note`),
  autoTranslate: (lexemeId, targetLang = 'en') => api.get(`/vocabulary/translate/${lexemeId}`, { params: { target_lang: targetLang } }),
  // New comprehensive word data APIs
  getWordData: (word, languageCode, isExpression = false, forceRefresh = false, interfaceLanguage = 'en', config = {}) => api.get(`/vocabulary/word-data/${encodeURIComponent(word)}`, { timeout: 12000, ...config, params: { language_code: languageCode, is_expression: isExpression, force_refresh: forceRefresh, interface_language: interfaceLanguage, cache_only: !forceRefresh, ...config.params } }),
  setWordStatus: (word, languageCode, status) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/status`, null, { params: { language_code: languageCode, status } }),
  setStatus: ({ word, normalized_form, language_code, status }) => api.post(`/vocabulary/word/${encodeURIComponent(normalized_form || word)}/status`, null, { params: { language_code, status } }),
  batchSetWordStatus: (words, languageCode, status = 'known') => api.post('/vocabulary/words/batch-set-status', { words, language_code: languageCode, status }),
  saveExpression: async (phrase, languageCode) => {
    // Enrich as expression then mark as 'learning' so it shows up in vocab/review
    await api.get(`/vocabulary/word-data/${encodeURIComponent(phrase)}`, {
      params: { language_code: languageCode, is_expression: true }
    });
    return api.post(`/vocabulary/word/${encodeURIComponent(phrase)}/status`, null, {
      params: { language_code: languageCode, status: 'learning' }
    });
  },
  saveWordToLanguage: (word, sourceLanguage, targetLanguage, markAsKnown = false) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/save-to-language`, null, { params: { source_language: sourceLanguage, target_language: targetLanguage, mark_as_known: markAsKnown } }),
  setPreferredDefinition: (word, languageCode, definitionIndex) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/set-preferred-definition`, null, { params: { language_code: languageCode, definition_index: definitionIndex } }),
  enrichWord: (word, languageCode, section = null, force = false, background = false, config = {}) => api.post('/vocabulary/enrich-word', null, { ...config, params: { word, language_code: languageCode, section, force, background } }),
  startReaderEnrich: (words, languageCode, priorityCount = 50) => api.post('/vocabulary/reader-enrich', { words, language_code: languageCode, priority_count: priorityCount }),
  getReaderEnrichStatus: (jobId) => api.get(`/vocabulary/reader-enrich/${jobId}`),
  finalizeWordData: (word, languageCode) => api.post('/vocabulary/finalize-word-data', null, { params: { word, language_code: languageCode } }),
  saveGlobalNote: (word, languageCode, notes) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/lexicon-note`, { notes }, { params: { language_code: languageCode } }),
  saveUserNote: (word, languageCode, note) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/user-note`, { note }, { params: { language_code: languageCode } }),
  unlockWord: (word, languageCode) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/unlock`, null, { params: { language_code: languageCode } }),
  setPreferredDefinition: (word, languageCode, index) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/set-preferred-definition`, null, { params: { language_code: languageCode, definition_index: index } }),
  removePreferredDefinition: (word, languageCode, index) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/remove-preferred-definition`, null, { params: { language_code: languageCode, definition_index: index } }),
  setWordImage: (word, languageCode, imageUrl) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/set-image`, { image_url: imageUrl }, { params: { language_code: languageCode } }),
  // V2 SRS: Real Exposure Tracking
  trackExposures: (exposures) => api.post('/vocabulary/track-exposure', { exposures }),
  trackCollocations: (word, languageCode, collocations, textId) => api.post('/vocabulary/track-collocations', { word, language_code: languageCode, collocations, text_id: textId }),
  getFlashcardLists: () => api.get('/vocabulary/flashcard-lists'),
  createFlashcardList: (data) => api.post('/vocabulary/flashcard-lists', data),
  addToFlashcardList: (listId, data) => api.post(`/vocabulary/flashcard-lists/${listId}/words`, data),
  getFlashcardListWords: (listId) => api.get(`/vocabulary/flashcard-lists/${listId}/words`),
  deleteFlashcardList: (listId) => api.delete(`/vocabulary/flashcard-lists/${listId}`),
  removeFromFlashcardList: (listId, wordId) => api.delete(`/vocabulary/flashcard-lists/${listId}/words/${wordId}`),
  copyFlashcardWord: (listId, wordId, targetListId) => api.post(`/vocabulary/flashcard-lists/${listId}/copy-word`, { word_id: wordId, target_list_id: targetListId }),
  moveFlashcardWord: (listId, wordId, targetListId) => api.post(`/vocabulary/flashcard-lists/${listId}/move-word`, { word_id: wordId, target_list_id: targetListId }),
  // Categories
  getUserCategories: (entityType) => api.get('/vocabulary/categories', { params: entityType ? { entity_type: entityType } : {} }),
  setEntityCategories: (entityType, entityId, categories) => api.put(`/vocabulary/categories/${entityType}/${entityId}`, { categories }),
  getEntityCategories: (entityType, entityId) => api.get(`/vocabulary/categories/${entityType}/${entityId}`),
  // Morphology
  getWordMorphology: (word, languageCode) => api.get(`/vocabulary/morphology/${encodeURIComponent(word)}`, { params: { language_code: languageCode } }),
  getWordMastery: (word, languageCode) => api.get(`/vocabulary/word-mastery/${encodeURIComponent(word)}`, { params: { language_code: languageCode } }),
  uploadWordImage: (word, languageCode, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/vocabulary/word/${encodeURIComponent(word)}/upload-image`, formData, {
      params: { language_code: languageCode },
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  removeWordImage: (word, languageCode) => api.delete(`/vocabulary/word/${encodeURIComponent(word)}/remove-image`, { params: { language_code: languageCode } }),
  // Batch operations
  batchUpdateStatus: (data) => api.post('/vocabulary/batch-update-status', data),
};

// Review API
export const reviewApi = {
  getQueue: (params = {}) => api.get('/review/queue', { params }),
  getQueueCount: (params = {}) => api.get('/review/queue/count', { params }),
  getQueueSummary: () => api.get('/review/queue/summary'),
  startSession: (data) => api.post('/review/session', data),
  submitAnswer: (data) => api.post('/review/answer', data),
  getHistory: (params = {}) => api.get('/review/history', { params }),
  getSessions: (params = {}) => api.get('/review/sessions', { params }),
  getSrsStats: (params = {}) => api.get('/review/srs-stats', { params }),
  getSrsSettings: () => api.get('/review/srs-settings'),
  updateSrsSettings: (data) => api.put('/review/srs-settings', data),
};

// Stats API
export const statsApi = {
  getOverview: () => api.get('/stats/overview'),
  getLanguageStats: () => api.get('/stats/languages'),
  getActivity: (params = {}) => api.get('/stats/activity', { params }),
  getTextStats: (textId) => api.get(`/stats/texts/${textId}`),
  getFrequencyProgress: (languageCode) => api.get(`/stats/frequency/${languageCode}`),
  getFrequencySummary: () => api.get('/stats/frequency-summary'),
  getMemoryAwareness: (languageCode) => api.get('/stats/memory-awareness', { params: languageCode ? { language_code: languageCode } : {} }),
  getSrsHealth: () => api.get('/stats/srs-health'),
  getStudyPlan: (budgetMinutes) => api.get('/stats/study-plan', { params: { budget: budgetMinutes } }),
  getPriorityAssessment: () => api.get('/stats/priority-assessment'),
  savePriorityAssessment: (assessments) => api.put('/stats/priority-assessment', { assessments }),
};

// Notifications API
export const notificationsApi = {
  getNotifications: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.patch('/notifications/mark-all-read'),
};

// Admin API
export const adminApi = {
  getEntries: (params = {}) => api.get('/admin/entries', { params }),
  getCount: () => api.get('/admin/entries/count'),
  updateEntry: (word, languageCode, data) => api.put(`/admin/entries/${encodeURIComponent(word)}`, { data }, { params: { language_code: languageCode } }),
  unlockEntry: (word, languageCode) => api.delete(`/admin/entries/${encodeURIComponent(word)}`, { params: { language_code: languageCode } }),
};

// Forge Sentences API (Admin)
export const forgeSentencesApi = {
  list: (params = {}) => api.get('/sentences', { params }),
  getStats: () => api.get('/sentences/stats'),
  create: (data) => api.post('/sentences', data),
  createBatch: (data) => api.post('/sentences/batch', data),
  get: (sentenceId) => api.get(`/sentences/${sentenceId}`),
  delete: (sentenceId) => api.delete(`/sentences/${sentenceId}`),
  regenerate: (sentenceId) => api.post(`/sentences/${sentenceId}/regenerate`),
  getLessonSentences: (courseId, lessonId, params = {}) => api.get(`/sentences/lesson/${courseId}/${lessonId}`, { params }),
  getAudio: (sentenceId, languageCode) => `${API}/sentences/${sentenceId}/audio/${languageCode}`,
  search: (query) => api.get('/sentences/search', { params: { q: query } }),
  // Edit endpoints
  update: (sentenceId, data) => api.put(`/sentences/${sentenceId}`, data),
  updateTranslation: (sentenceId, languageCode, data) => api.put(`/sentences/${sentenceId}/translation/${languageCode}`, data),
  generateAudio: (sentenceId, languageCode) => api.post(`/sentences/${sentenceId}/translation/${languageCode}/audio`),
  getByLocation: (courseId, lessonId, sentenceNum) => api.get(`/sentences/by-location/${courseId}/${lessonId}/${sentenceNum}`),
};

// Goals API
export const goalsApi = {
  getStreak: () => api.get('/goals/streak'),
  checkInStreak: () => api.post('/goals/streak/check-in'),
  getUserGoals: () => api.get('/goals/user'),
  getLanguageGoal: (languageId) => api.get(`/goals/language/${languageId}`),
  setLanguageGoal: (languageId, data) => api.post(`/goals/language/${languageId}`, data),
  getDailyMission: () => api.get('/goals/daily-mission'),
  getDynamicPlan: () => api.get('/goals/dynamic-plan'),
  getInsights: () => api.get('/goals/insights'),
};

// Onboarding API
export const onboardingApi = {
  getStatus: () => api.get('/onboarding/status'),
  complete: (data) => api.post('/onboarding/complete', data),
  getPreferences: () => api.get('/onboarding/preferences'),
  updatePreferences: (data) => api.put('/onboarding/preferences', data),
  getSampleTexts: (languageId, proficiency) => 
    api.get(`/onboarding/sample-texts?language_id=${languageId}&proficiency=${proficiency}`),
};

// Credits API (v2 — earning + spending)
export const creditsApi = {
  getSummary: () => api.get('/credits/summary'),
  earn: (event, metadata = {}) => api.post('/credits/earn', { event, metadata }),
};

// Forge Personalization API
export const forgePersonalizationApi = {
  getPersonalizedPath: (languageCode, course = 1) => api.get(`/forge/personalized/${languageCode}/${course}`),
  getPersonalVocab: (languageCode, limit = 20) => api.get(`/forge/personal-vocab/${languageCode}`, { params: { limit } }),
};

// Lexicon API (shared word database)
export const lexiconApi = {
  // Entry CRUD
  getEntry: (languageCode, word, entryType = 'word') => api.get(`/lexicon/entry/${languageCode}/${encodeURIComponent(word)}`, { params: { entry_type: entryType } }),
  createOrUpdate: (data) => api.post('/lexicon/entry', data),
  getStats: () => api.get('/lexicon/stats'),
  incrementUsage: (entryId) => api.post(`/lexicon/increment-usage/${entryId}`),

  // Field locking (admin)
  lockFields: (entryId, fields) => api.post(`/lexicon/entry/${entryId}/lock-fields`, fields),
  unlockFields: (entryId, fields) => api.post(`/lexicon/entry/${entryId}/unlock-fields`, fields),
  protectAll: (entryId) => api.post(`/lexicon/entry/${entryId}/protect-all`),
  unlockAll: (entryId) => api.post(`/lexicon/entry/${entryId}/unlock-all`),
  // Word-based field lock toggle (any user)
  toggleFieldLock: (word, languageCode, field) => api.post(`/vocabulary/word/${encodeURIComponent(word)}/toggle-field-lock`, null, { params: { language_code: languageCode, field } }),

  // Suggestions
  submitSuggestion: (entryId, suggestion) => api.post(`/lexicon/entry/${entryId}/suggestion`, suggestion),

  // Admin canonization
  getCanonizeQueue: (languageCode) => api.get('/lexicon/admin/canonize-queue', { params: languageCode ? { language_code: languageCode } : {} }),
  canonize: (entryId) => api.post(`/lexicon/admin/canonize/${entryId}`),
  unverify: (entryId) => api.post(`/lexicon/admin/unverify/${entryId}`),
  processSuggestion: (entryId, suggestionId, data) => api.post(`/lexicon/admin/process-suggestion/${entryId}/${suggestionId}`, data),

  // Expressions
  createExpression: (data) => api.post('/lexicon/expression', data),
  getExpressions: (languageCode) => api.get(`/lexicon/expressions/${languageCode}`),
  detectExpressions: (languageCode, tokens, textContent) => api.post('/lexicon/expressions/detect', { language_code: languageCode, tokens, text_content: textContent }),
  getExpression: (languageCode, surfaceForm) => api.get(`/lexicon/expression/${languageCode}/${encodeURIComponent(surfaceForm)}`),
  enrichExpression: (entryId) => api.post(`/lexicon/expression/${entryId}/enrich`),
  uploadExpressionImage: (entryId, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/lexicon/expression/${entryId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  removeExpressionImage: (entryId) => api.delete(`/lexicon/expression/${entryId}/image`),
  deleteExpression: (entryId) => api.delete(`/lexicon/expression/${entryId}`),
  updateExpression: (entryId, data) => api.patch(`/lexicon/expression/${entryId}`, data),
  setExpressionUserState: (entryId, data) => api.post(`/lexicon/expression/${entryId}/user-state`, data),
  getExpressionUserState: (entryId) => api.get(`/lexicon/expression/${entryId}/user-state`),

  // Verb Formations
  createVerbFormation: (data) => api.post('/lexicon/verb-formation', data),
  getVerbFormations: (languageCode) => api.get(`/lexicon/verb-formations/${languageCode}`),
  detectVerbFormations: (languageCode, tokens) => api.post('/lexicon/verb-formations/detect', { language_code: languageCode, tokens }),
  getVerbFormation: (languageCode, surfaceForm) => api.get(`/lexicon/verb-formation/${languageCode}/${encodeURIComponent(surfaceForm)}`),
  enrichVerbFormation: (entryId) => api.post(`/lexicon/verb-formation/${entryId}/enrich`),
  deleteVerbFormation: (entryId) => api.delete(`/lexicon/verb-formation/${entryId}`),
  updateVerbFormation: (entryId, data) => api.patch(`/lexicon/verb-formation/${entryId}`, data),
  setVerbFormationUserState: (entryId, data) => api.post(`/lexicon/verb-formation/${entryId}/user-state`, data),
  getVerbFormationUserState: (entryId) => api.get(`/lexicon/verb-formation/${entryId}/user-state`),
};

// Adaptive Text API (i+1)
export const adaptiveTextApi = {
  analyzeDifficulty: (textId) => api.get(`/texts/${textId}/difficulty`),
  getAdaptive: (textId) => api.post(`/texts/${textId}/adaptive`),
};

// Video Library API (admin + user)
export const videoLibraryApi = {
  import: (data) => api.post('/video-library/import', data),
  get: (videoId, lang) => api.get(`/video-library/${videoId}`, { params: { lang } }),
  listFeatured: (params = {}) => api.get('/video-library/featured/list', { params }),
  featuredLanguages: () => api.get('/video-library/featured/languages'),
  // Admin
  adminListAllFeatured: () => api.get('/video-library/featured/all'),
  seedFeatured: (seeds) => api.post('/video-library/featured/seed', { seeds }),
  hideFeatured: (externalId) => api.post(`/video-library/featured/${externalId}/hide`),
  unhideFeatured: (externalId) => api.post(`/video-library/featured/${externalId}/unhide`),
  deleteFeatured: (externalId, languageCode) =>
    api.delete(`/video-library/featured/${externalId}`, { params: languageCode ? { language_code: languageCode } : {} }),
  adminGetSubtitles: (videoId, languageCode) =>
    api.get(`/video-library/admin/${videoId}/subtitles`, { params: { language_code: languageCode } }),
  adminReplaceSubtitles: (videoId, languageCode, subtitlesText) =>
    api.put(`/video-library/admin/${videoId}/subtitles`, { subtitles_text: subtitlesText }, { params: { language_code: languageCode } }),
};

export default api;

// ── Sentence Q&A API (Forge) ──────────────────────────────────────────────────
export const sentenceQaApi = {
  getStatus: () => api.get('/sentence-qa/status'),
  listQuestions: (textId, sentenceIndex, params = {}) =>
    api.get(`/sentence-qa/sentences/${textId}/${sentenceIndex}/questions`, { params }),
  submitQuestion: (textId, sentenceIndex, payload) =>
    api.post(`/sentence-qa/sentences/${textId}/${sentenceIndex}/questions`, payload),
  pollQuestion: (questionId) => api.get(`/sentence-qa/questions/${questionId}`),
  regenerate: (questionId) => api.post(`/sentence-qa/questions/${questionId}/regenerate`),
  getExplanation: (textId, sentenceIndex) =>
    api.get(`/sentence-qa/sentences/${textId}/${sentenceIndex}/explanation`),
  // S23 — Helpfulness votes and reports
  voteExplanation: (explanationId, vote) =>
    api.post(`/sentence-qa/explanations/${explanationId}/vote`, { vote }),
  reportExplanation: (explanationId, reason) =>
    api.post(`/sentence-qa/explanations/${explanationId}/report`, { reason }),
  // S24 — Revision and withdrawal
  reviseExplanation: (explanationId, payload) =>
    api.patch(`/sentence-qa/explanations/${explanationId}/revise`, payload),
  getRevisions: (explanationId) =>
    api.get(`/sentence-qa/explanations/${explanationId}/revisions`),
  // S25 — Library
  getLibrary: (params = {}) => api.get('/sentence-qa/library', { params }),
  // S28 — Flashcard save
  saveToFlashcard: (explanationId) =>
    api.post(`/sentence-qa/explanations/${explanationId}/save-flashcard`),
  // S30 — Semantic similar
  getSimilar: (explanationId, params = {}) =>
    api.get(`/sentence-qa/explanations/${explanationId}/similar`, { params }),
  // S31 — Admin: duplicate candidates
  getDuplicateCandidates: (params = {}) =>
    api.get('/sentence-qa/admin/duplicate-candidates', { params }),
  // S32 — Admin: grammar gaps and inconsistency queue
  getGrammarGaps: (params = {}) =>
    api.get('/sentence-qa/admin/grammar-gaps', { params }),
  getInconsistencyQueue: (params = {}) =>
    api.get('/sentence-qa/admin/inconsistency-queue', { params }),
  // S33 — Cross-language links
  addCrossLanguageLink: (explanationId, payload) =>
    api.post(`/sentence-qa/explanations/${explanationId}/cross-language-links`, payload),
  getCrossLanguageLinks: (explanationId) =>
    api.get(`/sentence-qa/explanations/${explanationId}/cross-language-links`),
};
